import instanceAxios from '@/util/axios';

export interface ActionResult {
  ok: boolean;
  status: number;
  body: unknown;
}

/**
 * Runs a write the user approved. The id is the whole request — the arguments live server-side.
 *
 * `validateStatus` accepts every status so a refused write comes back as a value rather than a
 * thrown axios error. Without it, axios replaces the server's explanation with its own
 * "Request failed with status code 409" and the real reason is lost inside `error.response`.
 */
export const approveAgentAction = async (id: string): Promise<ActionResult> => {
  const { data, status } = await instanceAxios.post<ActionResult>(
    `pvt/agent/actions/${id}/approve`,
    undefined,
    { validateStatus: () => true }
  );

  // The endpoint mirrors the underlying call's status, so a body may be absent on a bare failure.
  return data ?? { ok: false, status, body: undefined };
};

/** Discards a proposal, so a later click cannot resurrect it. */
export const rejectAgentAction = async (id: string): Promise<void> => {
  await instanceAxios.post(`pvt/agent/actions/${id}/reject`, undefined, { validateStatus: () => true });
};

/**
 * What went wrong, in Hebrew, for the confirmation card.
 *
 * The backend splits its error bodies deliberately (see `utils/respondDomainError.ts`):
 * 400/409/502 carry a `message` written for a person, while 500 and 404 carry an `error` that is
 * an internal English string — "failed to duplicate product". So `message` is shown as-is and
 * `error` is never shown; anything without a usable message falls back to the status wording
 * below. `code` and `details` stay out of the UI too: they exist for the app to branch on, not
 * for a user to read.
 */
const STATUS_FALLBACK: Record<number, string> = {
  400: 'הפרטים שנשלחו אינם תקינים. נסה לנסח את הבקשה מחדש עם ערכים מלאים.',
  401: 'תוקף החיבור פג. התחבר מחדש ונסה שוב.',
  403: 'אין לך הרשאה לבצע את הפעולה הזאת.',
  404: 'הפעולה אינה קיימת עוד — היא פגה או שכבר בוצעה. בקש אותה שוב בצ׳אט.',
  409: 'הפעולה מתנגשת עם נתונים קיימים במערכת.',
  422: 'הפעולה נדחתה — הנתונים אינם עומדים בכללי המערכת.',
  502: 'שירות חיצוני לא זמין כרגע. נסה שוב בהמשך.'
};

export const describeActionFailure = (result: ActionResult): string => {
  const body = result.body;

  if (body && typeof body === 'object') {
    const { message } = body as { message?: unknown };
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return (
    STATUS_FALLBACK[result.status] ??
    (result.status >= 500
      ? 'התרחשה שגיאה בשרת. הפעולה לא בוצעה — נסה שוב, ואם זה חוזר פנה לתמיכה.'
      : 'הפעולה לא בוצעה.')
  );
};
